"use client";

import { ChevronLeft, ChevronRight } from "@/components/icons";
import { Button } from "@/components/ui/button";
import { useGetDaysContentQuery } from "@/features/course/courseSlice";
import { cn } from "@/lib/utils";
import { useParams, useRouter } from "next/navigation";

const LessonNavigation = ({ className }: { className?: string }) => {
  const params = useParams();
  const router = useRouter();
  const topicid = params.topicid;
  const dayId = params.dayid;

  const { data, isLoading, isFetching } = useGetDaysContentQuery(
    { id: dayId },
    { skip: !dayId }
  );

  const handleNavigation = (direction: "previous" | "next") => {
    if (!data) return;

    const currentIndex = data.findIndex(
      (item: any) => item.id === Number(topicid)
    );

    if (currentIndex === -1) return;

    if (direction === "previous" && currentIndex > 0) {
      const previousTopicId = data[currentIndex - 1].id;
      router.push(`/days/${dayId}/topics/${previousTopicId}`);
    } else if (direction === "next" && currentIndex < data.length - 1) {
      const nextTopicId = data[currentIndex + 1].id;
      router.push(`/days/${dayId}/topics/${nextTopicId}`);
    }
  };

  const isFirstTopic = () => {
    if (!data) return true;
    return data.findIndex((item: any) => item.id === Number(topicid)) === 0;
  };

  const isLastTopic = () => {
    if (!data) return true;
    const index = data.findIndex((item: any) => item.id === Number(topicid));
    return index === data.length - 1;
  };

  const isPrevLocked = () => {
    if (!data) return true;
    const currentIndex = data.findIndex(
      (item: any) => item.id === Number(topicid)
    );

    // If there's no previous item or current is first, return true (disabled)
    if (currentIndex <= 0) return true;

    // Return the locked status of the previous item
    return data[currentIndex - 1].is_lock;
  };

  const isNextLocked = () => {
    if (!data) return true;
    const currentIndex = data.findIndex(
      (item: any) => item.id === Number(topicid)
    );

    // If there's no next item or current is last, return true (disabled)
    if (currentIndex === -1 || currentIndex >= data.length - 1) return true;

    // Return the locked status of the next item
    return data[currentIndex + 1].is_lock;
  };

  if (isLoading || isFetching) {
    return (
      <div className={cn("my-6 flex items-center justify-between", className)}>
        <Button type="button" variant="outline" disabled>
          Previous
        </Button>
        <Button type="button" variant="secondary" disabled>
          Next
        </Button>
      </div>
    );
  }

  return (
    <div className={cn("my-6 flex items-center justify-between", className)}>
      <Button
        type="button"
        variant="outline"
        onClick={() => handleNavigation("previous")}
        disabled={isFirstTopic() || isPrevLocked()}
      >
        <ChevronLeft />
        Previous
      </Button>

      <Button
        type="button"
        variant="secondary"
        onClick={() => handleNavigation("next")}
        disabled={isLastTopic() || isNextLocked()}
      >
        Next
        <ChevronRight />
      </Button>
    </div>
  );
};

export default LessonNavigation;
